export const QUOTE_READER_VERSION = 2;
export const QUOTE_UPDATE_REQUIRED = 'Application updated. Reload to continue. Your unsaved changes have not been discarded.';
export const QUOTE_RECOVERY_PAUSED = 'Saving patterned quotations is paused in recovery mode. Your unsaved changes are preserved. Compatible saved quotations can still be viewed and exported.';
export const QUOTE_MAINTENANCE_PAUSED = 'This legacy quotation maintenance operation is paused for snapshot compatibility. No records were changed. Use exact quotation diagnosis or the existing index-based reports.';

export function quoteFailureNotice(error = {}) {
  const code = String(error.code || '').replace(/^functions\//, '');
  const detail = error.details?.code;
  if (code === 'quote/update-required' || detail === 'QUOTE_CLIENT_UPDATE_REQUIRED') return { kind: 'update-required', message: QUOTE_UPDATE_REQUIRED, tone: 'warning' };
  if (code === 'quote/recovery-paused') return { kind: 'recovery-paused', message: QUOTE_RECOVERY_PAUSED, tone: 'warning' };
  if (code === 'quote/maintenance-paused') return { kind: 'maintenance-paused', message: error.message || QUOTE_MAINTENANCE_PAUSED, tone: 'warning' };
  if (code === 'quote/data-unavailable' || ['QUOTE_DATA_INCOMPLETE', 'QUOTE_REVISION_MISSING', 'QUOTE_REVISION_CHANGED'].includes(detail)) return {
    kind: 'data-unavailable', message: 'This saved quotation could not be read consistently. Reopen it or contact support before continuing. Your unsaved changes are preserved.', tone: 'warning' };
  if (code === 'permission-denied') return { kind: 'permission-denied', message: 'You do not have permission to open this quotation. Contact your workspace administrator. Your draft has not been discarded.', tone: 'error' };
  if (['unavailable', 'deadline-exceeded', 'internal'].includes(code)) return { kind: 'server-error', message: 'The server could not complete this quotation operation. Your unsaved changes are preserved. Try again when the service is available.', tone: 'error' };
  return null;
}

export function canonicalQuotationIdentity(ref) {
  const parts = String(ref?.path || '').split('/');
  return parts.length === 4 && parts[0] === 'users' && parts[2] === 'quotations'
    ? { workspaceUid: parts[1], quoteId: parts[3] } : null;
}

export function assertQuoteDocumentIdentity(quote = {}, workspaceUid, quoteId) {
  if (quoteReaderRequirement(quote) < 2) return quote;
  const ids = ['id', 'quoteId', 'sourceQuoteId', 'quoteIndexId', 'indexId'].map(key => quote[key]).filter(value => value != null && value !== '');
  if (!quoteId || ids.some(value => value !== quoteId)
    || quote.workspaceUid && quote.workspaceUid !== workspaceUid
    || quote.sourcePath && quote.sourcePath !== `users/${workspaceUid}/quotations/${quoteId}`) {
    const error = new Error('Quotation identities do not agree. No quotation was opened or changed. Run exact diagnosis before continuing.');
    error.code = 'quote/data-unavailable';
    throw error;
  }
  return quote;
}

// Every exact document consumer uses this boundary, including maintenance reads.
// Quotation bodies are never served from the application's TTL cache.
export function createQuotationDocumentReader({ readRaw, callLoader }) {
  return (ref, options = {}) => {
    const identity = canonicalQuotationIdentity(ref);
    return identity ? readCompatibleQuoteDocument({ ref, workspaceUid: identity.workspaceUid,
      getDoc: value => readRaw(value, options), callLoader }) : readRaw(ref, options);
  };
}

export function assertLegacyRepairSupported(quote = {}) {
  if (quoteReaderRequirement(quote) >= 2 || quote.rowStorageMode === 'subcollection_v3') {
    const error = new Error('This protected quotation can be diagnosed, but this legacy transactional repair cannot safely update it. No repair was made.');
    error.code = 'quote/maintenance-paused';
    throw error;
  }
}

export function requiresPatternWriter(payload = {}) {
  const quote = payload.quoteHeader || payload.quotePayload || {};
  if (quoteReaderRequirement(quote) >= 2) return true;
  const rows = payload.rows || quote.rows || [];
  const repeats = [...Object.values(quote.repeatSettings || {}), ...rows.map(row => row.repeatSettings || {})];
  return rows.some(row => ['pattern-repeat-v2', 'legacy-repeat-v1'].includes(row.calculationSnapshot?.repeatCuttingVersion))
    || repeats.some(r => (r.verticalEnabled && Number(r.verticalRepeatInch || r.verticalRepeatIn) > 0)
      || (r.horizontalEnabled && Number(r.horizontalRepeatInch || r.horizontalRepeatIn) > 0));
}

export function quoteReaderRequirement(quote = {}) {
  return Math.max(Number(quote.minimumReaderVersion) || 0,
    quote.quotationSnapshotVersion === 'pattern-repeat-v2' ? 2 : 0);
}

export function assertCompatibleQuote(quote = {}, readerVersion = QUOTE_READER_VERSION) {
  if (quote.minimumReaderVersion != null && (!Number.isInteger(Number(quote.minimumReaderVersion)) || Number(quote.minimumReaderVersion) < 0)) {
    const error = new Error('Invalid quotation snapshot metadata.');
    error.code = 'quote/data-unavailable';
    throw error;
  }
  const unknown = quote.quotationSnapshotVersion && !['pattern-repeat-v2', 'legacy'].includes(quote.quotationSnapshotVersion);
  if (unknown || quoteReaderRequirement(quote) > readerVersion) {
    const error = new Error(QUOTE_UPDATE_REQUIRED);
    error.code = 'quote/update-required';
    throw error;
  }
  return quote;
}

// Capability negotiation is not authorization: the callable separately checks
// active workspace membership and quotation permissions. Never trust this as a role.
export async function readCompatibleQuoteDocument({ ref, index = {}, workspaceUid, getDoc, callLoader }) {
  assertCompatibleQuote(index);
  assertQuoteDocumentIdentity(index, workspaceUid, ref.id);
  const canonical = async () => {
    let result;
    try { result = await callLoader({ workspaceUid, quoteId: ref.id, quoteReaderVersion: QUOTE_READER_VERSION }); }
    catch (error) {
      const notice = quoteFailureNotice(error);
      if (notice) error.message = notice.message;
      throw error;
    }
    const body = result.data ?? result;
    if (!body.ok || !body.quote) {
      const error = new Error('Quotation not found.'); error.code = 'not-found'; throw error;
    }
    assertCompatibleQuote(body.quote);
    assertQuoteDocumentIdentity(body.quote, workspaceUid, ref.id);
    return { id: ref.id, exists: () => true, data: () => body.quote, canonicalRowsHydrated: true };
  };
  if (quoteReaderRequirement(index) >= 2) return canonical();
  try {
    const snap = await getDoc(ref);
    if (snap.exists()) {
      assertCompatibleQuote(snap.data());
      if (snap.data().rowStorageMode === 'subcollection_v3' && !snap.canonicalRowsHydrated) return canonical();
    }
    return snap;
  } catch (error) {
    if (String(error.code || '').includes('permission-denied')) return canonical();
    throw error;
  }
}
